import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ColorsV2,
  FieldLabel,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Spacer,
  Text,
  TextInput,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useUnenrollDeviceMutation } from '@/redux/slices/mdmManagementApiSlice';
import { DeviceAsset } from '@/types/assets';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

type UnenrollDeviceModalProps = {
  deviceAsset: DeviceAsset;
  employeeName: string;
  onCompleteUnenroll: () => void;
  onCancel: () => void;
};

const UnenrollDeviceModal = ({
  deviceAsset,
  employeeName,
  onCompleteUnenroll,
  onCancel,
}: UnenrollDeviceModalProps) => {
  const { t } = useTranslation('people');
  const organizationId = useGetOrganizationId();
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [unenrollDevice, { isLoading }] = useUnenrollDeviceMutation();

  const serialNumber = deviceAsset.serialNumber;

  const handleInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = event => {
    const newValue = event.target.value;
    setButtonEnabled(newValue.toLowerCase() === serialNumber?.toLowerCase());
  };

  const handleUnenrollDevice = async () => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID for device not found');
      }
      await unenrollDevice({
        organizationId,
        deviceId: deviceAsset.device.id || '',
      }).unwrap();
      showSuccessToast({
        id: 'unenroll-device-success-toast',
        message: employeeName
          ? t('employeeProfile.modals.unenrollDevice.successMessage', {
              employeeName,
            })
          : t(
              'employeeProfile.modals.unenrollDevice.successMessageWithoutEmployee'
            ),
      });
      onCompleteUnenroll();
    } catch (error) {
      showErrorToast({
        id: 'unenroll-device-error-toast',
        message: t('employeeProfile.modals.unenrollDevice.errorMessage'),
      });
    }
  };

  return (
    <ModalV2 visible hide={onCancel} ariaLabelledby="unenroll-device">
      <ModalHeader>
        <Text variant="heading-2">
          {employeeName
            ? t('employeeProfile.modals.unenrollDevice.title', { employeeName })
            : t('employeeProfile.modals.unenrollDevice.titleWithoutEmployee')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text>
          {employeeName
            ? t('employeeProfile.modals.unenrollDevice.description', {
                employeeName,
              })
            : t(
                'employeeProfile.modals.unenrollDevice.descriptionWithoutEmployee'
              )}
        </Text>
        <Spacer />
        <Text>
          {t('employeeProfile.modals.unenrollDevice.confirmInstructions')}
        </Text>
        <Spacer />
        <FieldLabel>
          <Text color={ColorsV2.ERROR} pr="1">
            *
          </Text>
          <Text variant="label-small">
            {t('employeeProfile.modals.unenrollDevice.serialNumberLabel', {
              serialNumber,
            })}
          </Text>
        </FieldLabel>
        <TextInput
          autoComplete="off"
          id="unenroll-device-serial-number-input"
          placeholder={t(
            'employeeProfile.modals.unenrollDevice.serialNumberPlaceholder'
          )}
          onChange={handleInputChange}
        />
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="unenroll-device-cancel"
            onClick={onCancel}
            variant="text"
            loading={isLoading}>
            {t('employeeProfile.modals.unenrollDevice.cancel')}
          </ModalAction>
          <ModalAction
            id="unenroll-device-submit"
            onClick={handleUnenrollDevice}
            disabled={!buttonEnabled}
            loading={isLoading}
            intent="danger">
            {t('employeeProfile.modals.unenrollDevice.confirm')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default UnenrollDeviceModal;
